/**
 * Language preference plumbing for the English | Hinglish site toggle.
 *
 * Design rules (locked):
 *  - English is the default for anyone without an explicit saved choice.
 *  - We NEVER auto-detect from geo, `navigator.language`, or Accept-Language.
 *  - The choice is stored in BOTH a cookie (so SSR can render the right
 *    language and avoid a flash of English) and localStorage (belt and
 *    braces for cookie-cleared browsers).
 *  - This is a preference-only toggle: URLs, canonicals and hreflang are
 *    untouched.
 */

export type Lang = "en" | "hi-en";

/**
 * Master switch for the Hinglish rollout. While this is `false` the site is
 * English for EVERYONE — including visitors who previously switched to
 * Hinglish: their saved choice is ignored and actively cleared on the next
 * visit, so nothing is left behind when the toggle comes back.
 */
export const HINGLISH_ENABLED = false;

export const DEFAULT_LANG: Lang = "en";
export const LANG_COOKIE = "truwe_lang";
export const LANG_STORAGE_KEY = "truwe.lang";
/** One year — an explicit choice should survive across visits. */
export const LANG_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export const LANG_LABELS: Record<Lang, string> = {
  en: "English",
  "hi-en": "Hinglish",
};

export function isLang(value: unknown): value is Lang {
  return value === "en" || value === "hi-en";
}

/** Parse a raw `Cookie:` header (or `document.cookie`) for the saved choice. */
export function langFromCookieHeader(header: string | null | undefined): Lang | null {
  if (!HINGLISH_ENABLED) return null;
  if (!header) return null;
  for (const part of header.split(";")) {
    const [rawKey, ...rest] = part.split("=");
    if (rawKey?.trim() !== LANG_COOKIE) continue;
    const value = decodeURIComponent(rest.join("=").trim());
    return isLang(value) ? value : null;
  }
  return null;
}

/** Forget any saved choice (cookie + localStorage). No-op on the server. */
export function clearSavedLang(): void {
  if (typeof document === "undefined") return;
  try {
    document.cookie = `${LANG_COOKIE}=; path=/; max-age=0; samesite=lax`;
  } catch {
    /* cookies blocked */
  }
  try {
    window.localStorage.removeItem(LANG_STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

/** Persist an explicit choice. No-op on the server. */
export function persistLang(lang: Lang): void {
  if (typeof document === "undefined") return;
  if (!HINGLISH_ENABLED) {
    clearSavedLang();
    return;
  }
  try {
    document.cookie = `${LANG_COOKIE}=${lang}; path=/; max-age=${LANG_COOKIE_MAX_AGE}; samesite=lax`;
  } catch {
    /* cookies blocked — localStorage below still carries the choice */
  }
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    /* private mode */
  }
}

/** Read the saved choice in the browser. Returns null when nothing is saved. */
export function readSavedLang(): Lang | null {
  if (typeof document === "undefined") return null;
  if (!HINGLISH_ENABLED) {
    // Roll anyone who had switched to Hinglish back to English, once.
    clearSavedLang();
    return null;
  }
  const fromCookie = langFromCookieHeader(document.cookie);
  if (fromCookie) return fromCookie;
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    return isLang(stored) ? stored : null;
  } catch {
    return null;
  }
}
